// ─── Files on a case, shown rather than downloaded ──────────────────────────
//
// A screenshot is half of every support ticket, and a screenshot you have to
// download and open in another window is a screenshot nobody looks at until
// they have to. Images render in the thread and in the Files rail; clicking
// one opens it large, with Download beside it. PDFs and text files stay as
// downloads — a PDF opened in the CRM's own origin can carry script, and
// these bytes may have come from a customer.
//
// WHY THIS IS SAFE TO RENDER. Every file on a case passed the byte sniffer in
// squish-crm-backend lib/case-attachments.js (and its twin on the customer
// side): only JPEG, PNG, GIF, WebP, AVIF, PDF and plain text are ever stored.
// An .exe, a .zip, a .docx, an SVG or an HTML file is refused at upload with
// 415 whatever its name says, so nothing here can meet a file that is not
// one of those seven. The image is fetched with the staff headers and shown
// from a blob: URL in an <img> — the browser decodes pixels and nothing else.
//
// The bytes are cached per file id for the life of the tab: a case with the
// same screenshot in the thread and in the rail fetches it once.

import React, { useEffect, useState, useCallback } from 'react';
import { getHeaders } from '../api';
import { isImageFile, humanBytes, filesMentioned } from '../lib/caseAttachments';

export { isImageFile, humanBytes, filesMentioned };

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// file id → { url } | { error } | Promise
const blobCache = new Map();

export async function fetchAttachmentBlob(caseId, file) {
  if (blobCache.has(file.id)) return blobCache.get(file.id); // a settled entry or the in-flight promise
  const p = (async () => {
    const res = await fetch(`${API}/api/cases/${caseId}/attachments/${file.id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not load the file');
    const blob = await res.blob();
    return { url: URL.createObjectURL(blob), blob };
  })();
  blobCache.set(file.id, p);
  try {
    const out = await p;
    blobCache.set(file.id, out);
    return out;
  } catch (e) {
    blobCache.delete(file.id);
    throw e;
  }
}

export async function downloadAttachment(caseId, file) {
  const { url } = await fetchAttachmentBlob(caseId, file);
  const a = document.createElement('a');
  a.href = url; a.download = file.name || 'attachment';
  document.body.appendChild(a); a.click(); a.remove();
}

function useAttachmentUrl(caseId, file, enabled) {
  const [state, setState] = useState({ url: null, error: null });
  useEffect(() => {
    if (!enabled || !file?.id) return undefined;
    let alive = true;
    fetchAttachmentBlob(caseId, file)
      .then(({ url }) => alive && setState({ url, error: null }))
      .catch((e) => alive && setState({ url: null, error: e.message }));
    return () => { alive = false; };
  }, [caseId, file?.id, enabled]);
  return state;
}

// ─── One file, as a card ──────────────────────────────────────────────────────
// `size` 'thumb' (thread) or 'row' (rail). Images show themselves; anything
// else is a labelled chip. Clicking an image opens it large; clicking a
// non-image downloads it.
export function AttachmentCard({ caseId, file, size = 'thumb', onOpen, onError }) {
  const image = isImageFile(file);
  const { url, error } = useAttachmentUrl(caseId, file, image);
  const [busy, setBusy] = useState(false);

  const act = async () => {
    if (image) { onOpen?.(file); return; }
    setBusy(true);
    try { await downloadAttachment(caseId, file); } catch (e) { onError?.(e.message); }
    setBusy(false);
  };

  const label = (
    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, minWidth: 0, width: '100%', alignItems: 'baseline' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)', fontSize: 12, fontWeight: 600 }}>
        {image ? '🖼' : '📎'} {file.name}
      </span>
      <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
        {humanBytes(file.bytes)}{!image ? ' · download' : ''}
      </span>
    </span>
  );

  if (size === 'row') {
    return (
      <button
        type="button"
        onClick={act}
        disabled={busy}
        title={image ? `Open ${file.name}` : `Download ${file.name}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          padding: 6, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
          cursor: busy ? 'progress' : 'pointer', fontFamily: 'var(--font)',
        }}
      >
        <span style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 6, background: 'var(--bg3)', overflow: 'hidden', display: 'grid', placeItems: 'center', color: 'var(--text3)', fontSize: 16 }}>
          {image && url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : image ? '…' : '📄'}
        </span>
        {label}
      </button>
    );
  }

  // thumb — in the thread, under the comment that mentioned it
  return (
    <button
      type="button"
      onClick={act}
      disabled={busy}
      title={image ? `Open ${file.name}` : `Download ${file.name}`}
      style={{
        display: 'inline-flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', textAlign: 'left',
        maxWidth: 360, padding: 6, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
        cursor: busy ? 'progress' : 'pointer', fontFamily: 'var(--font)',
      }}
    >
      {image && (
        <span style={{ display: 'block', borderRadius: 6, overflow: 'hidden', background: 'var(--bg3)', minHeight: 80, maxHeight: 260 }}>
          {url
            ? <img src={url} alt={file.name} style={{ display: 'block', maxWidth: '100%', maxHeight: 260, objectFit: 'contain', margin: '0 auto' }} />
            : <span style={{ display: 'grid', placeItems: 'center', height: 80, fontSize: 11, color: error ? 'var(--accent2)' : 'var(--text3)', fontFamily: 'var(--mono)', padding: '0 10px', textAlign: 'center' }}>{error || 'Loading image…'}</span>}
        </span>
      )}
      {label}
    </button>
  );
}

// ─── The file, large ──────────────────────────────────────────────────────────
export function Lightbox({ caseId, file, onClose, onError }) {
  const { url, error } = useAttachmentUrl(caseId, file, true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    // Capture phase so this Escape never reaches the panel's own handler.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const download = useCallback(async () => {
    setBusy(true);
    try { await downloadAttachment(caseId, file); } catch (e) { onError?.(e.message); }
    setBusy(false);
  }, [caseId, file, onError]);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-label={file.name}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}
    >
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, cursor: 'default', maxWidth: '90vw' }}>
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🖼 {file.name}</span>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontFamily: 'var(--mono)' }}>{humanBytes(file.bytes)}</span>
        <button onClick={download} disabled={busy} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
          {busy ? 'Saving…' : 'Download'}
        </button>
        <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
      </div>
      {url
        ? <img src={url} alt={file.name} onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 12px 48px rgba(0,0,0,0.6)', cursor: 'default' }} />
        : <div style={{ color: error ? '#fca5a5' : 'rgba(255,255,255,0.7)', fontFamily: 'var(--mono)', fontSize: 12 }}>{error || 'Loading…'}</div>}
    </div>
  );
}
